import React, { useCallback, useMemo } from 'react'
import { Text, Button, ButtonProps, TextProps as TextBaseProps } from 'rebass'
import { NavLink as RouterLink, NavLinkProps } from 'react-router-dom'
import { StyledButton } from '../NavButton'
import { noop } from 'lodash'
import { theme } from 'theme'
export interface TextProps extends TextBaseProps {
    key?: string
}
export interface ButtonBaseProps extends ButtonProps {
    key?: string | number
    textProps?: TextProps
    disabled?: boolean
    isActive?: boolean
    hasShadow?: boolean
}
interface WithLinkProps extends NavLinkProps {
    disabled?: boolean
}

const ButtonBase: React.FC<ButtonBaseProps> = ({
    children,
    textProps,
    hasShadow,
    disabled = false,
    isActive = false,
    onClick = noop,
    bg = theme.newColors.neutral50,
    py = 2,
    px = 4,
    sx,
    ...props
}) => {
    const mergedSx = useMemo(
        () => ({
            fontFamily: 'inherit',
            cursor: isActive ? 'default' : 'pointer',
            boxShadow: hasShadow
                ? '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)'
                : undefined,
            '&:disabled': {
                cursor: 'not-allowed',
            },
            '&:focus': {
                outline: 'none',
            },
            ...sx,
        }),
        [isActive, hasShadow, sx],
    )
    return (
        <Button
            bg={bg}
            py={py}
            px={px}
            opacity={disabled ? 0.4 : 1}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            sx={mergedSx}
            {...props}
        >
            <Text fontSize={1} color={theme.colors.astronaut} fontWeight="bold" {...textProps}>
                {children}
            </Text>
        </Button>
    )
}

const withLinkWrapper: <T extends object>(component: React.FC<T>) => React.FC<T & WithLinkProps> = Component =>
    function LinkWrapperHOC({ to, activeClassName, ...props }): JSX.Element {
        const isDisabled = props.disabled
        const preventRedirect = useCallback(e => e.preventDefault(), [])
        return (
            <RouterLink
                to={to}
                activeClassName={activeClassName}
                // there is no `disabled` attribute for anchor tag,
                // so we need to prevent re-directing by this
                onClick={isDisabled ? preventRedirect : undefined}
            >
                <Component {...(props as any)} />
            </RouterLink>
        )
    }

export default ButtonBase
export const ButtonLink = withLinkWrapper(ButtonBase)
export const NavButtonLink = withLinkWrapper(StyledButton)
export { ButtonBase, withLinkWrapper, StyledButton }
